import { useEffect, type RefObject } from "react";

import { applyBoardViewportTouchScrollDelta, type BoardPinchZoomUpdate } from "./boardTouchScroll";
import {
  commitBoardTouchTap,
  initBoardTouchTapTracking,
  markBoardTouchTapMoved,
  resetBoardTouchTapTracking,
  type BoardTouchTap,
} from "./boardTouchTapTracking";
import { computePinchScrollTargets, computePinchZoom } from "./boardViewport";

type BoardPinchZoomHandlers = {
  getZoom: () => number;
  getMinZoom: () => number;
  onZoom: (update: BoardPinchZoomUpdate) => void;
};

type GestureState = {
  lastClientX: number;
  lastClientY: number;
  panning: boolean;
  pinching: boolean;
  hadPinch: boolean;
  tapStartX: number;
  tapStartY: number;
  tapMoved: boolean;
  pinchStartDistance: number;
  pinchStartZoom: number;
  pinchFocal: { x: number; y: number };
  pinchStart: {
    scrollLeft: number;
    windowScrollY: number;
    viewportLeft: number;
    canvasTop: number;
  };
};

type GestureContext = {
  viewport: HTMLDivElement;
  canvas: HTMLCanvasElement;
  zoom: BoardPinchZoomHandlers;
  onTap?: (tap: BoardTouchTap) => void;
  state: GestureState;
};

function touchDistance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function touchMidpoint(a: Touch, b: Touch): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

function beginPan(state: GestureState, touch: Touch): void {
  state.lastClientX = touch.clientX;
  state.lastClientY = touch.clientY;
  state.panning = true;
}

function resetTouchGestureState(state: GestureState): void {
  state.panning = false;
  state.pinching = false;
  resetBoardTouchTapTracking(state);
}

function beginPinch({ viewport, canvas, zoom, state }: GestureContext, event: TouchEvent): void {
  const [a, b] = [event.touches[0], event.touches[1]];
  state.panning = false;
  state.pinching = true;
  state.hadPinch = true;
  state.pinchStartDistance = touchDistance(a, b);
  state.pinchStartZoom = zoom.getZoom();
  state.pinchFocal = touchMidpoint(a, b);
  state.pinchStart = {
    scrollLeft: viewport.scrollLeft,
    windowScrollY: window.scrollY,
    viewportLeft: viewport.getBoundingClientRect().left,
    canvasTop: canvas.getBoundingClientRect().top,
  };
}

function handlePinchMove({ zoom, state }: GestureContext, event: TouchEvent): void {
  const nextZoom = computePinchZoom({
    startZoom: state.pinchStartZoom,
    startDistance: state.pinchStartDistance,
    distance: touchDistance(event.touches[0], event.touches[1]),
    minZoom: zoom.getMinZoom(),
  });
  const targets = computePinchScrollTargets({
    startZoom: state.pinchStartZoom,
    zoom: nextZoom,
    focal: state.pinchFocal,
    start: state.pinchStart,
  });
  zoom.onZoom({ zoom: nextZoom, ...targets });
  event.preventDefault();
}

function handlePanMove({ viewport, state }: GestureContext, event: TouchEvent): void {
  const touch = event.touches[0];
  markBoardTouchTapMoved(state, touch);
  const deltaX = state.lastClientX - touch.clientX;
  const deltaY = state.lastClientY - touch.clientY;
  state.lastClientX = touch.clientX;
  state.lastClientY = touch.clientY;
  applyBoardViewportTouchScrollDelta({ viewport, deltaX, deltaY });
  event.preventDefault();
}

function preventNativePinchZoom(viewport: HTMLDivElement): () => void {
  const prevent = (event: Event) => event.preventDefault();
  viewport.addEventListener("gesturestart", prevent);
  viewport.addEventListener("gesturechange", prevent);
  viewport.addEventListener("gestureend", prevent);
  return () => {
    viewport.removeEventListener("gesturestart", prevent);
    viewport.removeEventListener("gesturechange", prevent);
    viewport.removeEventListener("gestureend", prevent);
  };
}

function handleTouchStart(ctx: GestureContext, event: TouchEvent): void {
  const { state } = ctx;
  if (event.touches.length >= 2) {
    beginPinch(ctx, event);
    return;
  }
  if (event.touches.length !== 1) return;
  initBoardTouchTapTracking(state, event.touches[0]);
  beginPan(state, event.touches[0]);
}

function handleTouchMove(ctx: GestureContext, event: TouchEvent): void {
  const { state } = ctx;
  if (event.touches.length >= 2) {
    if (!state.pinching) beginPinch(ctx, event);
    handlePinchMove(ctx, event);
    return;
  }
  if (state.panning && event.touches.length === 1) {
    handlePanMove(ctx, event);
  }
}

function handleTouchEnd(ctx: GestureContext, event: TouchEvent): void {
  const { onTap, state } = ctx;
  if (event.touches.length === 0) {
    commitBoardTouchTap({ state, event, onTap });
    resetTouchGestureState(state);
    return;
  }
  if (event.touches.length === 1) {
    state.pinching = false;
    beginPan(state, event.touches[0]);
  }
}

function attachDecoupledTouchGestures(ctx: GestureContext): () => void {
  const { viewport, canvas } = ctx;
  viewport.style.touchAction = "none";
  canvas.style.touchAction = "none";
  const detachGesturePrevention = preventNativePinchZoom(viewport);

  const onTouchStart = (event: TouchEvent) => handleTouchStart(ctx, event);
  const onTouchMove = (event: TouchEvent) => handleTouchMove(ctx, event);
  const onTouchEnd = (event: TouchEvent) => handleTouchEnd(ctx, event);

  viewport.addEventListener("touchstart", onTouchStart, { passive: true });
  viewport.addEventListener("touchmove", onTouchMove, { passive: false });
  viewport.addEventListener("touchend", onTouchEnd);
  viewport.addEventListener("touchcancel", onTouchEnd);

  return () => {
    viewport.style.touchAction = "";
    canvas.style.touchAction = "";
    detachGesturePrevention();
    viewport.removeEventListener("touchstart", onTouchStart);
    viewport.removeEventListener("touchmove", onTouchMove);
    viewport.removeEventListener("touchend", onTouchEnd);
    viewport.removeEventListener("touchcancel", onTouchEnd);
  };
}

function createGestureState(): GestureState {
  return {
    lastClientX: 0,
    lastClientY: 0,
    panning: false,
    pinching: false,
    hadPinch: false,
    tapStartX: 0,
    tapStartY: 0,
    tapMoved: false,
    pinchStartDistance: 0,
    pinchStartZoom: 1,
    pinchFocal: { x: 0, y: 0 },
    pinchStart: { scrollLeft: 0, windowScrollY: 0, viewportLeft: 0, canvasTop: 0 },
  };
}

export function useBoardViewportTouchGestures({
  viewportRef,
  canvasRef,
  enabled,
  getZoom,
  getMinZoom,
  onZoom,
  onTap,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enabled: boolean;
  getZoom: () => number;
  getMinZoom: () => number;
  onZoom: (update: BoardPinchZoomUpdate) => void;
  onTap?: (tap: BoardTouchTap) => void;
}): void {
  useEffect(() => {
    if (!enabled) return;
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;
    return attachDecoupledTouchGestures({
      viewport,
      canvas,
      zoom: { getZoom, getMinZoom, onZoom },
      onTap,
      state: createGestureState(),
    });
  }, [enabled, viewportRef, canvasRef, getZoom, getMinZoom, onTap, onZoom]);
}
